import type { FC } from "react";
import { Breadcrumb, Container, Image } from "react-bootstrap";
import { Trans, useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import IconArrowBack from "components/Icons/IconArrowBack/IconArrowBack";
import IconCheck from "images/icons/check.svg";

/** Konkrétní produkt v Zóně, ne rozcestník — na rozcestníku student kurz hledal sám. */
const PURCHASE_URL = "https://zona.to-das.cz/_u_app13.php";

/**
 * AIDEV-NOTE: Ceny (`completeCourse.upgradePrice` a `fullPrice` v common.json) jsou ruční
 * duplikát ceníku Zóny. GraphQL API ceník nezná — `User.fullAccess` je jediný boolean —
 * takže když se cena v Zóně změní, tuhle aplikaci na to nikdo neupozorní. Při doplnění
 * ceníku do API oba klíče zmizí. [TD-374]
 *
 * AIDEV-NOTE: Nadpis je vlastní `<h1>`, ne `PageHeading`. `PageHeading` bere nadpis
 * z `usePageTitle`, kde slug `kompletni-kurz` po TD-374 mapování nemá — bez `customTitle`
 * by `<h1>` zůstal prázdný, s ním by se název vypsal dvakrát („Kompletní kurz / Kompletní
 * kurz"). [TD-374]
 */
const CompleteCoursePage: FC = () => {
  const { t } = useTranslation(["common"]);

  const benefits = [
    t("completeCourse.benefitBooks"),
    t("completeCourse.benefitExam"),
    t("completeCourse.benefitMasterclass"),
    t("completeCourse.benefitMock"),
  ];

  return (
    <>
      <div className="page-header">
        <Container className="container-mw-md page-container">
          <Breadcrumb>
            <li className="breadcrumb-item">
              <Link to="/">
                <IconArrowBack />
                {t("goHome")}
              </Link>
            </li>
          </Breadcrumb>

          <h1>{t("completeCourse.heading")}</h1>
        </Container>
      </div>

      <Container className="container-mw-md page-container">
        <div className="complete-course">
          <Image src="/images/capy-graduation.svg" alt="" className="complete-course__capybara" />

          <h2 className="complete-course__heading">{t("completeCourse.cardHeading")}</h2>
          <p className="complete-course__lead">{t("completeCourse.lead")}</p>

          <ul className="complete-course__benefits">
            {benefits.map(benefit => (
              <li key={benefit} className="complete-course__benefit">
                <IconCheck aria-hidden />
                <span>{benefit}</span>
              </li>
            ))}
          </ul>

          {/* Odkaz stylovaný jako tlačítko — Zóna je jiná aplikace, proto <a>, ne <Link>.
              Vzor: features/DashboardCalendarOverview/DashboardCalendarOverview.tsx:109
              AIDEV-NOTE: Bez vlastní `complete-course__cta` třídy záměrně — mezeru od seznamu
              benefitů (24 px podle Figmy, uzly 13168:48438/48470) dává celá `margin-bottom`
              na `.complete-course__benefits`; `&__benefit:last-child` v `_complete-course.scss`
              nuluje marži posledního `<li>`, aby se nepřičítala navrch (flex item zakládá
              vlastní formátovací kontext, marže ven nekolabuje — viz AIDEV-NOTE tam). Přidaný
              `margin-top` na tomhle odkazu by mezeru zvětšil nad 24 px. Ověřeno renderem
              (headless Chrome proti kompilovanému CSS, viz report Tasku 8). [TD-374] */}
          <a href={PURCHASE_URL} target="_blank" rel="noopener noreferrer" className="btn btn-primary">
            {t("completeCourse.cta")}
          </a>

          <p className="complete-course__price">
            <Trans
              i18nKey="completeCourse.priceDifference"
              ns="common"
              values={{ price: t("completeCourse.upgradePrice") }}
              components={{ bold: <strong /> }}
            />
          </p>
          <p className="complete-course__price-note">
            {t("completeCourse.priceFull", { price: t("completeCourse.fullPrice") })}
          </p>
        </div>
      </Container>
    </>
  );
};

export default CompleteCoursePage;
